import { useMemo, useState } from "react";

import { useApiPlaylist, limitVideos } from "../shared/useApiPlaylist";
import { ShadowHost } from "../shared/ShadowHost";
import { ReelsGrid } from "./ReelsGrid";
import { ReelsPlayer } from "./ReelsPlayer";
import { getReelsSheet } from "./sheet";
import type { VideoItem, CtaOverlay } from "../shared/types";

/** Pre-fetched mode: supply a `videos` array directly. */
type WithVideos = { videos: VideoItem[]; apiKey?: never; playlistId?: never };
/** API-driven mode: supply credentials and the embed fetches the playlist. */
type WithApi = { apiKey: string; playlistId: string; videos?: never };

export type ReelsEmbedProps = (WithVideos | WithApi) & {
  /** CTA image overlays applied to every video in the player. */
  overlays?: CtaOverlay[];
  /** Number of videos to fetch per page in API-driven mode. Defaults to 30. Ignored in pre-fetched mode. */
  pageSize?: number;
  /** Maximum number of videos to show. Applies to both modes; in API-driven mode, pagination stops once the limit is reached. Leave unset for no limit. */
  maxVideos?: number;
};

export function ReelsEmbed({
  overlays = [],
  pageSize,
  maxVideos,
  ...props
}: ReelsEmbedProps) {
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);
  const sheets = useMemo(() => [getReelsSheet()], []);

  const apiKey =
    "apiKey" in props && props.apiKey != null ? props.apiKey : null;
  const playlistId =
    "playlistId" in props && props.playlistId != null ? props.playlistId : null;
  const isApiDriven = apiKey != null && playlistId != null;
  const apiState = useApiPlaylist(apiKey, playlistId, pageSize, maxVideos);

  // Pagination only applies in API-driven mode — pre-fetched `videos` is the whole list.
  const videos =
    "videos" in props && props.videos != null
      ? limitVideos(props.videos, maxVideos)
      : apiState.status === "success"
        ? apiState.videos
        : null;
  const hasNextPage =
    isApiDriven && apiState.status === "success" ? apiState.hasNextPage : false;

  if (apiState.status === "loading") {
    return (
      <ShadowHost sheets={sheets}>
        <div className="seeen-reels-skeleton">
          {Array.from({ length: 9 }, (_, i) => (
            <div key={i} className="seeen-reels-skeleton-cell" />
          ))}
        </div>
      </ShadowHost>
    );
  }

  if (apiState.status === "error") {
    return (
      <ShadowHost sheets={sheets}>
        <div className="seeen-reels-error">Failed to load playlist.</div>
      </ShadowHost>
    );
  }

  if (!videos || videos.length === 0) return null;

  return (
    <ShadowHost sheets={sheets}>
      <ReelsGrid
        videos={videos}
        onSelect={setSelectedIndex}
        hasNextPage={hasNextPage}
        isLoadingMore={apiState.isLoadingMore}
        onLoadMore={apiState.loadMore}
      />
      {selectedIndex !== null && (
        <ReelsPlayer
          videos={videos}
          initialIndex={selectedIndex}
          overlays={overlays}
          onClose={() => setSelectedIndex(null)}
        />
      )}
    </ShadowHost>
  );
}
